import type { ComponentProps } from "react";
import { FoodsTags } from "@foodpilot/foods";
import { getApiViolations } from "src/utils/apiViolations";
import { SsoProtocol } from "src/pages/Authentication/ssoConnector";

export const PROVIDER_PROTOCOL_LABELS: Record<SsoProtocol, string> = {
    saml2: "SAML 2.0",
    cas3: "CAS 3",
    oauth: "OAuth 2.0",
};

export type ProviderFieldKind = "text" | "textarea" | "keyvalue" | "list";

export type ProviderFieldFormat = "url" | "path" | "certificate";

export type ProviderFieldGroup =
    | "identity"
    | "button"
    | "credentials"
    | "endpoints"
    | "idp"
    | "sp"
    | "server"
    | "advanced";

export type ProviderFieldConfig = {
    key: string;
    kind: ProviderFieldKind;
    group: ProviderFieldGroup;
    required: boolean;
    format?: ProviderFieldFormat;
    lockedOnEdit?: boolean;
};

export const OAUTH_SECRET_FIELD_KEY = "clientSecret" as const;

export const MIN_CERTIFICATE_LENGTH = 50;

export const PROVIDER_PROTOCOLS = ["oauth2", "oidc", "oauth2_jwt"] as const;
export type ProviderProtocol = (typeof PROVIDER_PROTOCOLS)[number];

export const DEFAULT_PROVIDER_PROTOCOL: ProviderProtocol = "oauth2";

export const TRANSITIONAL_PROVIDER_PROTOCOL: ProviderProtocol = "oauth2_jwt";

export const PROVIDER_PROTOCOL_MODE_LABELS: Record<ProviderProtocol, string> = {
    oauth2: "OAuth 2.0",
    oidc: "OAuth 2.0 · OIDC",
    oauth2_jwt: "OAuth 2.0 · transitional",
};

export const readProviderProtocol = (raw: unknown): ProviderProtocol =>
    PROVIDER_PROTOCOLS.find((protocol) => protocol === raw) ?? DEFAULT_PROVIDER_PROTOCOL;

const PROVIDER_NAME_FIELD: ProviderFieldConfig = {
    key: "providerName",
    kind: "text",
    group: "identity",
    required: true,
    lockedOnEdit: true,
};

export const LABEL_FIELD_KEY = "label" as const;

const LABEL_FIELD: ProviderFieldConfig = {
    key: LABEL_FIELD_KEY,
    kind: "text",
    group: "button",
    required: false,
};

export const PROVIDER_FIELD_CONFIGS: Record<SsoProtocol, ProviderFieldConfig[]> = {
    oauth: [],
    saml2: [
        PROVIDER_NAME_FIELD,
        LABEL_FIELD,
        { key: "idpEntityId", kind: "text", group: "idp", required: true },
        { key: "idpSsoUrl", kind: "text", group: "idp", required: true, format: "url" },
        { key: "idpSloUrl", kind: "text", group: "idp", required: true, format: "url" },
        { key: "idpCert", kind: "textarea", group: "idp", required: true, format: "certificate" },
        { key: "spEntityId", kind: "text", group: "sp", required: true, format: "url" },
        { key: "spAcsUrl", kind: "text", group: "sp", required: true, format: "url" },
        { key: "spSloUrl", kind: "text", group: "sp", required: true, format: "url" },
        { key: "identifierAttribute", kind: "text", group: "sp", required: true },
        { key: "redirectUrl", kind: "text", group: "advanced", required: false, format: "url" },
    ],
    cas3: [
        PROVIDER_NAME_FIELD,
        LABEL_FIELD,
        { key: "hostUrl", kind: "text", group: "server", required: true, format: "url" },
        { key: "loginUrl", kind: "text", group: "server", required: true, format: "path" },
        { key: "logoutUrl", kind: "text", group: "server", required: true, format: "path" },
        { key: "validationServiceUrl", kind: "text", group: "server", required: true, format: "path" },
        { key: "resourceOwnerMapping", kind: "keyvalue", group: "advanced", required: false },
    ],
};

export const GROUP_ORDER: ProviderFieldGroup[] = [
    "identity",
    "button",
    "credentials",
    "endpoints",
    "idp",
    "sp",
    "server",
    "advanced",
];

const OAUTH_COMMON_FIELDS: ProviderFieldConfig[] = [
    PROVIDER_NAME_FIELD,
    LABEL_FIELD,
    { key: "clientId", kind: "text", group: "credentials", required: true },
];

const OAUTH_TRAILING_FIELDS: ProviderFieldConfig[] = [
    { key: "redirectUrl", kind: "text", group: "advanced", required: false, format: "url" },
    { key: "resourceOwnerMapping", kind: "keyvalue", group: "advanced", required: false },
    { key: "cookiesToSaveOnPersistence", kind: "list", group: "advanced", required: false },
];

const OAUTH_FIELDS_BY_PROTOCOL: Record<ProviderProtocol, ProviderFieldConfig[]> = {
    oauth2: [
        ...OAUTH_COMMON_FIELDS,
        { key: "urlAuthorize", kind: "text", group: "endpoints", required: true, format: "url" },
        { key: "urlAccessToken", kind: "text", group: "endpoints", required: true, format: "url" },
        { key: "urlResourceOwnerDetails", kind: "text", group: "endpoints", required: true, format: "url" },
        { key: "scopes", kind: "text", group: "endpoints", required: true },
        { key: "externalIdCookieName", kind: "text", group: "advanced", required: false },
        { key: "resourceOwnerHeaders", kind: "keyvalue", group: "advanced", required: false },
        ...OAUTH_TRAILING_FIELDS,
    ],
    oidc: [
        ...OAUTH_COMMON_FIELDS,
        { key: "metadataUrl", kind: "text", group: "endpoints", required: true, format: "url" },
        { key: "scopes", kind: "text", group: "endpoints", required: true },
        ...OAUTH_TRAILING_FIELDS,
    ],
    oauth2_jwt: [
        ...OAUTH_COMMON_FIELDS,
        { key: "metadataUrl", kind: "text", group: "endpoints", required: true, format: "url" },
        { key: "scopes", kind: "text", group: "endpoints", required: true },
        ...OAUTH_TRAILING_FIELDS,
    ],
};

export const getProviderFields = (
    protocol: SsoProtocol,
    mode: ProviderProtocol = DEFAULT_PROVIDER_PROTOCOL,
): ProviderFieldConfig[] => (protocol === "oauth" ? OAUTH_FIELDS_BY_PROTOCOL[mode] : PROVIDER_FIELD_CONFIGS[protocol]);

export const getProviderGroups = (protocol: SsoProtocol, mode?: ProviderProtocol): ProviderFieldGroup[] => {
    const present = new Set(getProviderFields(protocol, mode).map((field) => field.group));

    return GROUP_ORDER.filter((group) => present.has(group));
};

export const getGroupFields = (
    protocol: SsoProtocol,
    group: ProviderFieldGroup,
    mode?: ProviderProtocol,
): ProviderFieldConfig[] => getProviderFields(protocol, mode).filter((field) => field.group === group);

export type ProviderFormValues = Record<string, string>;

export type FieldIssue = "required" | "url" | "path" | "certificate" | "duplicate" | "name";

export type FieldIssues = Record<string, FieldIssue>;

export const isAbsoluteUrl = (value: string): boolean => /^https?:\/\/[^\s]+$/.test(value);

export const isAbsolutePath = (value: string): boolean => value.startsWith("/");

export const isSafeProviderName = (value: string): boolean => /^[A-Za-z0-9._~:-]+$/.test(value);

const formatIssue = (field: ProviderFieldConfig, value: string): FieldIssue | undefined => {
    if (field.format === "url" && !isAbsoluteUrl(value)) return "url";
    if (field.format === "path" && !isAbsolutePath(value)) return "path";
    if (field.format === "certificate" && value.length < MIN_CERTIFICATE_LENGTH) return "certificate";

    return undefined;
};

export type ExistingProvider = {
    protocol: SsoProtocol;
    providerName: string;
};

type ValidateOptions = {
    currentName?: string;
    existingProviders: ExistingProvider[];
    isCreate: boolean;
    protocol: SsoProtocol;
    values: ProviderFormValues;
};

export const readMode = (values: ProviderFormValues): ProviderProtocol => readProviderProtocol(values.protocol);

export const validateProviderForm = (options: ValidateOptions): FieldIssues => {
    const { currentName, existingProviders, isCreate, protocol, values } = options;
    const issues: FieldIssues = {};

    for (const field of getProviderFields(protocol, readMode(values))) {
        const value = (values[field.key] ?? "").trim();

        if (value === "") {
            if (field.required) issues[field.key] = "required";
            continue;
        }

        const issue = formatIssue(field, value);
        if (issue !== undefined) issues[field.key] = issue;
    }

    const name = (values.providerName ?? "").trim();
    const isTakenOnThisProtocol = existingProviders.some(
        (existing) =>
            existing.protocol === protocol && existing.providerName === name && existing.providerName !== currentName,
    );

    if (name !== "" && !isSafeProviderName(name)) {
        issues.providerName = "name";
    } else if (name !== "" && isTakenOnThisProtocol) {
        issues.providerName = "duplicate";
    }

    if (protocol === "oauth" && isCreate && (values[OAUTH_SECRET_FIELD_KEY] ?? "").trim() === "") {
        issues[OAUTH_SECRET_FIELD_KEY] = "required";
    }

    return issues;
};

export const mapViolationsToFields = (error: unknown): Record<string, string> => {
    const violations = getApiViolations(error);

    if (violations === null) return {};

    return violations.reduce<Record<string, string>>((mapped, violation) => {
        const path = violation.propertyPath;
        const message = violation.message;

        if (path === undefined || path === "" || message === undefined || message === "") return mapped;
        if (mapped[path] !== undefined) return mapped;

        return { ...mapped, [path]: message };
    }, {});
};

export type MappingValue = string | string[];

export const LIST_VALUED_MAPPING_KEYS = new Set(["json_attributes"]);

export const parseKeyValueText = (text: string): Record<string, MappingValue> => {
    const result: Record<string, MappingValue> = {};

    text.split("\n").forEach((line) => {
        const separatorIndex = line.indexOf(":");
        if (separatorIndex === -1) return;

        const key = line.slice(0, separatorIndex).trim();
        const value = line.slice(separatorIndex + 1).trim();
        if (key === "") return;

        result[key] = LIST_VALUED_MAPPING_KEYS.has(key) ? parseCommaList(value) : value;
    });

    return result;
};

export const formatKeyValueText = (map: Record<string, MappingValue> | null | undefined): string => {
    if (map === null || map === undefined) return "";

    return Object.entries(map)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? formatCommaList(value) : value}`)
        .join("\n");
};

export const parseCommaList = (text: string): string[] => {
    return text
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "");
};

export const formatCommaList = (list: string[] | null | undefined): string => {
    if (list === null || list === undefined) return "";

    return list.join(", ");
};

const CALLBACK_URL_TEMPLATES: Record<SsoProtocol, (apiOrigin: string, providerName: string) => string> = {
    oauth: (apiOrigin, providerName) => `${apiOrigin}/api/auth/callback/${providerName}`,
    saml2: (apiOrigin, providerName) => `${apiOrigin}/api/saml2/acs/${providerName}`,
    cas3: (apiOrigin, providerName) => `${apiOrigin}/api/cas3/callback/${providerName}`,
};

export const buildProviderCallbackUrl = (protocol: SsoProtocol, providerName: string, apiOrigin: string): string => {
    const trimmed = providerName.trim();

    if (trimmed === "") return "";

    return CALLBACK_URL_TEMPLATES[protocol](apiOrigin, trimmed);
};

export const SAML2_ACS_FIELD_KEY = "spAcsUrl" as const;

export const isAcsUrlMismatched = (values: ProviderFormValues, expectedAcsUrl: string): boolean => {
    const current = (values[SAML2_ACS_FIELD_KEY] ?? "").trim();

    return current !== "" && expectedAcsUrl !== "" && current !== expectedAcsUrl;
};

export const providerToFormValues = (protocol: SsoProtocol, provider?: Record<string, unknown>): ProviderFormValues => {
    const values: ProviderFormValues = {};
    const mode = readProviderProtocol(provider?.protocol);

    if (protocol === "oauth") {
        values.protocol = mode;
    }

    for (const field of getProviderFields(protocol, mode)) {
        const raw = provider?.[field.key];

        if (field.kind === "keyvalue") {
            values[field.key] = formatKeyValueText(raw as Record<string, MappingValue> | null | undefined);
        } else if (field.kind === "list") {
            values[field.key] = formatCommaList(raw as string[] | null | undefined);
        } else {
            values[field.key] = typeof raw === "string" ? raw : "";
        }
    }

    if (protocol === "oauth") {
        values[OAUTH_SECRET_FIELD_KEY] = "";
    }

    return values;
};

export const buildProviderPayload = (protocol: SsoProtocol, values: ProviderFormValues): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    const mode = readMode(values);

    if (protocol === "oauth") {
        payload.protocol = mode;
    }

    for (const field of getProviderFields(protocol, mode)) {
        const raw = values[field.key] ?? "";

        if (field.kind === "keyvalue") {
            const parsed = parseKeyValueText(raw);
            payload[field.key] = Object.keys(parsed).length === 0 ? null : parsed;
        } else if (field.kind === "list") {
            const parsed = parseCommaList(raw);
            payload[field.key] = parsed.length === 0 ? null : parsed;
        } else {
            const trimmed = raw.trim();
            payload[field.key] = trimmed === "" ? null : trimmed;
        }
    }

    if (protocol === "oauth") {
        const secret = (values[OAUTH_SECRET_FIELD_KEY] ?? "").trim();
        if (secret !== "") payload[OAUTH_SECRET_FIELD_KEY] = secret;
    }

    return payload;
};

export const carryOverModeValues = (previous: ProviderFormValues, next: ProviderProtocol): ProviderFormValues => {
    const shared = new Set(getProviderFields("oauth", next).map((field) => field.key));
    const kept = Object.entries(previous).filter(([key]) => shared.has(key));

    return {
        ...providerToFormValues("oauth", { protocol: next }),
        ...Object.fromEntries(kept),
        [OAUTH_SECRET_FIELD_KEY]: previous[OAUTH_SECRET_FIELD_KEY] ?? "",
        protocol: next,
    };
};

export type ProviderTagVariant = ComponentProps<typeof FoodsTags>["variant"];

export const getProviderTagVariant = (mode: ProviderProtocol | null): ProviderTagVariant =>
    mode === TRANSITIONAL_PROVIDER_PROTOCOL ? "tags-coral" : "tags-grey";

export const readOauthMode = (protocol: SsoProtocol, mode: unknown): ProviderProtocol | null =>
    protocol === "oauth" ? readProviderProtocol(mode) : null;

export const getProviderTagLabel = (protocol: SsoProtocol, mode: ProviderProtocol | null): string =>
    mode === null ? PROVIDER_PROTOCOL_LABELS[protocol] : PROVIDER_PROTOCOL_MODE_LABELS[mode];
